import React, { useState, useEffect, useMemo } from 'react';
import { MdEdit, MdDelete, MdSearch } from 'react-icons/md';
import { FaCheckCircle, FaTimesCircle, FaUserPlus, FaTimes } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api`;

const UserMaster = () => {
  /* ---------- STATE ---------- */
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const [form, setForm] = useState({
    username: '', password: '', address: '', phone: '', guardianName: '',
    qualification: '', dateOfBirth: '', gender: '', dateofjoining: '',
    aadharCardNumber: '', email: '', role: '', branch: '', remarks: '', isactive: true,
  });

  /* ---------- HELPERS ---------- */
  const token = () => localStorage.getItem('token');
  const headers = () => ({
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  });

  /* ---------- API CALLS ---------- */
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [uRes, rRes, bRes] = await Promise.all([
        fetch(`${API_BASE_URL}/master/user`, { headers: headers() }),
        fetch(`${API_BASE_URL}/role/roles`, { headers: headers() }),
        fetch(`${API_BASE_URL}/master/branch`, { headers: headers() }),
      ]);
      const uData = await uRes.json();
      const rData = await rRes.json();
      const bData = await bRes.json();

      setUsers(uData.data || []);
      setRoles(rData.data || []);
      setBranches(bData.data || []);
    } catch (err) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token()) fetchAll();
  }, []);

  /* ---------- SEARCH & PAGINATION ---------- */
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return users;
    return users.filter(u =>
      (u.username || '').toLowerCase().includes(term) ||
      (u.email || '').toLowerCase().includes(term) ||
      (u.role?.roleName || '').toLowerCase().includes(term)
    );
  }, [users, search]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  /* ---------- HANDLERS ---------- */
  const openModal = (user = null) => {
    if (user) {
      setEditingId(user._id);
      setForm({
        username: user.username || '',
        password: '', // Leave blank for PUT
        address: user.address || '',
        phone: user.phone || '',
        guardianName: user.guardianName || '',
        qualification: user.qualification || '',
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
        gender: user.gender || '',
        dateofjoining: user.dateofjoining ? user.dateofjoining.split('T')[0] : '',
        aadharCardNumber: user.aadharCardNumber || '',
        email: user.email || '',
        role: user.role?._id || user.role || '',
        branch: user.branch?._id || user.branch || '',
        remarks: user.remarks || '',
        isactive: user.isactive ?? true,
      });
    } else {
      setEditingId(null);
      setForm({
        username: '', password: '', address: '', phone: '', guardianName: '',
        qualification: '', dateOfBirth: '', gender: '', dateofjoining: '',
        aadharCardNumber: '', email: '', role: '', branch: '', remarks: '', isactive: true,
      });
    }
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.username || (!editingId && !form.password) || !form.email || !form.phone || !form.role || !form.branch) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      // Create a clean payload
      const payload = { ...form };

      // IMPORTANT: Remove password on PUT if it's empty
      if (editingId && !payload.password) {
        delete payload.password;
      }

      // Avoid duplicate key error for empty Aadhar number
      if (!payload.aadharCardNumber || payload.aadharCardNumber.trim() === '') {
        delete payload.aadharCardNumber;
      }

      const url = editingId ? `${API_BASE_URL}/master/user/${editingId}` : `${API_BASE_URL}/master/user`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (res.ok || result.success) {
        toast.success(editingId ? "User updated" : "User created");
        fetchAll();
        setModalOpen(false);
      } else {
        const errorMsg = result.error || result.message || "Operation failed";
        if (errorMsg.includes("duplicate key error")) {
          if (errorMsg.includes("aadharCardNumber")) {
            toast.error("Aadhar Card Number already exists.");
          } else if (errorMsg.includes("email")) {
            toast.error("Email already exists.");
          } else if (errorMsg.includes("username")) {
            toast.error("Username already exists.");
          } else {
            toast.error("Duplicate entry found.");
          }
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/master/user/${id}`, {
        method: 'DELETE',
        headers: headers()
      });
      if (res.ok) {
        toast.success("User removed");
        fetchAll();
      }
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer />
      <h2 className="text-2xl font-bold mb-6 text-gray-800 uppercase">User Master</h2>

      <div className="bg-white rounded-xl shadow-lg p-6">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 border-b pb-6">
          <div className="relative w-full md:w-96">
            <MdSearch className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
            />
          </div>
          <button
            onClick={() => openModal()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-bold shadow-md"
          >
            ADD NEW USER
          </button>
        </div>

        {/* Table UI */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full table-auto border-collapse">
            <thead className="bg-blue-600 text-white text-xs uppercase">
              <tr>
                <th className="p-4 text-left">S.No</th>
                <th className="p-4 text-left">Name / Email</th>
                <th className="p-4 text-left">Role</th>
                <th className="p-4 text-left">Branch</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              {paginated.map((u, i) => {
                const sNo = search
                  ? users.findIndex(orig => orig._id === u._id) + 1
                  : (page - 1) * perPage + i + 1;

                return (
                  <tr key={u._id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 text-gray-600 font-bold">{sNo}</td>
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-800 uppercase">{u.username}</span>
                        <span className="text-xs text-blue-600">{u.email}</span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-700 font-medium">{u.role?.roleName || '-'}</td>
                    <td className="p-4 text-gray-700">{u.branch?.branchname || '-'}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${u.isactive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                        {u.isactive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-3">
                        <button onClick={() => openModal(u)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"><MdEdit size={18} /></button>
                        <button onClick={() => remove(u._id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"><MdDelete size={18} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <span className="text-sm text-gray-600">Showing {paginated.length} of {filtered.length} users</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 bg-gray-100 border rounded-lg text-xs font-bold hover:bg-gray-200 disabled:opacity-50">PREV</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 bg-gray-100 border rounded-lg text-xs font-bold hover:bg-gray-200 disabled:opacity-50">NEXT</button>
            </div>
          </div>
        )}
      </div>

      {/* FULL MODAL FORM */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative p-8">
            <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500"><FaTimes size={24} /></button>
            <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-4">{editingId ? 'Edit User' : 'Add New User'}</h2>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Username *</label>
                <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{editingId ? 'New Password (Optional)' : 'Password *'}</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="******" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Phone *</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Role *</label>
                <select
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full p-2 border rounded-lg outline-none"
                >
                  <option value="">Select Role</option>
                  {roles
                    // Filter out the 'customer' role (case-insensitive)
                    .filter(r => r.roleName.toLowerCase() !== 'customer')
                    .map(r => (
                      <option key={r._id} value={r._id}>
                        {r.roleName}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Branch *</label>
                <select value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} className="w-full p-2 border rounded-lg outline-none">
                  <option value="">Select Branch</option>
                  {branches.map(b => <option key={b._id} value={b._id}>{b.branchname}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Aadhar Number</label>
                <input value={form.aadharCardNumber} onChange={e => setForm({ ...form, aadharCardNumber: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">DOB</label>
                <input type="date" value={form.dateOfBirth} onChange={e => setForm({ ...form, dateOfBirth: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Joining Date</label>
                <input type="date" value={form.dateofjoining} onChange={e => setForm({ ...form, dateofjoining: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Address</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Gender</label>
                <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })} className="w-full p-2 border rounded-lg outline-none">
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="md:col-span-3 flex items-center gap-2 py-4">
                <input type="checkbox" id="isactive" checked={form.isactive} onChange={e => setForm({ ...form, isactive: e.target.checked })} className="w-5 h-5 text-blue-600 rounded" />
                <label htmlFor="isactive" className="text-sm font-bold text-gray-700">Account Active</label>
              </div>

              <div className="md:col-span-3 flex justify-end gap-3 border-t pt-6">
                <button type="button" onClick={() => setModalOpen(false)} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-bold">CANCEL</button>
                <button type="submit" className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold shadow-lg">SAVE USER</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMaster;